import type { TableProps, ColumnsType } from "antd/es/table";
import { Card, Pagination, Spin, Table, Typography } from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import {
  Component,
  ReactNode,
  useMemo,
  useState,
  type ErrorInfo,
} from "react";
import { useMediaQuery } from "@mui/material";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import i18n from "~/i18n";

const { Text } = Typography;

const DEFAULT_MOBILE_PAGE_SIZE = 10;

const spacing = { inline: 8, component: 16, page: 24 };
const colors = {
  actionPrimary: "#1677ff",
  borderSubtle: "#f0f0f0",
  textSecondary: "#8c8c8c",
};
const borderRadius = { card: 8 };
const fontSize = { body: 14, caption: 12 };

export interface MobileCardConfig {
  titleKey?: string;
  subtitleKey?: string;
  primaryKeys?: string[];
  actionsKey?: string;
  hideDetails?: boolean;
}

export interface MobileDefaultSortConfig {
  dataIndex: string | string[];
  order: "ascend" | "descend";
}

interface CustomTableProps {
  tableHeaders: ReactNode;
  tableProps: TableProps<any>;
  footer?: ReactNode;
  style?: React.CSSProperties;
  mobileCardConfig?: MobileCardConfig | false;
  forceMobileView?: boolean;
  mobileDefaultSort?: MobileDefaultSortConfig;
}

const getNestedValue = (obj: any, path: string | string[]): any => {
  const keys = Array.isArray(path) ? path : path.split(".");
  return keys.reduce((acc, key) => acc?.[key], obj);
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  !("$$typeof" in (value as object));

/** Reject plain objects that would crash React; keep elements/primitives. */
const sanitizeCellOutput = (value: unknown): ReactNode => {
  if (isPlainObject(value)) {
    return "-";
  }
  if (Array.isArray(value) && value.some(isPlainObject)) {
    return "-";
  }
  return value as ReactNode;
};

/** Format a raw (no column.render) cell value for mobile cards. */
const formatRawCellValue = (value: unknown): ReactNode => {
  if (value == null || value === "") {
    return "-";
  }
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return String(value);
  }
  if (isPlainObject(value) || Array.isArray(value)) {
    return "-";
  }
  return value as ReactNode;
};

class MobileCardErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Mobile card render failed:", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Card
          size="small"
          style={{
            marginBottom: spacing.inline,
            borderRadius: borderRadius.card,
          }}
          styles={{ body: { padding: spacing.component } }}
        >
          <Text type="secondary">
            {i18n.t("Unable_to_display_this_row")}
          </Text>
        </Card>
      );
    }
    return this.props.children;
  }
}

const MobileCardItem = ({
  record,
  columns,
  config,
}: {
  record: any;
  columns: ColumnsType<any>;
  config: MobileCardConfig;
}) => {
  const { t } = useAppTranslation();
  const [expanded, setExpanded] = useState(false);

  const findColumn = (key: string) =>
    columns.find((col: any) => col.key === key || col.dataIndex === key);

  const titleColumn = findColumn(config.titleKey || "") || (columns[0] as any);
  const subtitleColumn = config.subtitleKey
    ? findColumn(config.subtitleKey)
    : null;
  const actionsColumn = findColumn(config.actionsKey || "actions");
  const primaryKeys = config.primaryKeys || [];
  const primaryColumns = primaryKeys
    .map((key) => findColumn(key))
    .filter(Boolean);

  const excludeKeys = new Set([
    titleColumn?.key || (titleColumn as any)?.dataIndex,
    subtitleColumn?.key || (subtitleColumn as any)?.dataIndex,
    actionsColumn?.key || (actionsColumn as any)?.dataIndex,
    ...primaryKeys,
  ]);
  const detailColumns = columns.filter(
    (col: any) => !excludeKeys.has(col.key) && !excludeKeys.has(col.dataIndex),
  );

  const renderColumnValue = (column: any, row: any): ReactNode => {
    const dataIndex = column.dataIndex;
    const value = dataIndex ? getNestedValue(row, dataIndex) : row;
    if (column.render) {
      try {
        return sanitizeCellOutput(column.render(value, row, 0));
      } catch (error) {
        console.error("Mobile card column render failed:", error);
        return "-";
      }
    }
    return formatRawCellValue(value);
  };

  const titleValue = renderColumnValue(titleColumn, record);
  const subtitleValue = subtitleColumn
    ? renderColumnValue(subtitleColumn, record)
    : null;
  const actionsValue = actionsColumn
    ? renderColumnValue(actionsColumn, record)
    : null;
  const hasDetails = detailColumns.length > 0 && !config.hideDetails;

  return (
    <Card
      size="small"
      style={{
        marginBottom: spacing.inline,
        borderRadius: borderRadius.card,
      }}
      styles={{ body: { padding: spacing.component } }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: spacing.inline,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: fontSize.body,
              fontWeight: 600,
              display: "block",
            }}
          >
            {titleValue}
          </div>
          {subtitleValue ? (
            <div
              style={{
                fontSize: fontSize.caption,
                color: colors.textSecondary,
              }}
            >
              {subtitleValue}
            </div>
          ) : null}
        </div>
        {actionsValue ? (
          <div style={{ flexShrink: 0 }}>{actionsValue}</div>
        ) : null}
      </div>

      {primaryColumns.length > 0 ? (
        <div style={{ marginTop: spacing.inline }}>
          {primaryColumns.map((column: any, idx) => (
            <div
              key={column.key || column.dataIndex || idx}
              style={{
                display: "flex",
                gap: spacing.inline,
                fontSize: fontSize.caption,
                marginTop: idx > 0 ? 4 : 0,
              }}
            >
              <Text type="secondary" style={{ minWidth: 80, flexShrink: 0 }}>
                {column.title}:
              </Text>
              <div style={{ flex: 1, minWidth: 0 }}>
                {renderColumnValue(column, record)}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {hasDetails ? (
        <div style={{ marginTop: spacing.inline }}>
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              cursor: "pointer",
              color: colors.actionPrimary,
              fontSize: fontSize.caption,
              padding: "4px 0",
              border: "none",
              background: "none",
            }}
          >
            {expanded ? (
              <DownOutlined style={{ fontSize: 10 }} />
            ) : (
              <RightOutlined style={{ fontSize: 10 }} />
            )}
            <span>
              {expanded ? t("Hide_details") : t("Show_details")}
            </span>
          </button>
          {expanded ? (
            <div
              style={{
                marginTop: spacing.inline,
                paddingTop: spacing.inline,
                borderTop: `1px solid ${colors.borderSubtle}`,
              }}
            >
              {detailColumns.map((column: any, idx) => (
                <div
                  key={column.key || column.dataIndex || idx}
                  style={{
                    display: "flex",
                    gap: spacing.inline,
                    fontSize: fontSize.caption,
                    marginTop: idx > 0 ? 4 : 0,
                  }}
                >
                  <Text
                    type="secondary"
                    style={{ minWidth: 100, flexShrink: 0 }}
                  >
                    {column.title}:
                  </Text>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {renderColumnValue(column, record)}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
};

const resolveMobilePagination = (
  pagination: TableProps<any>["pagination"],
  total: number,
) => {
  if (pagination === false) {
    return null;
  }

  const config =
    pagination && typeof pagination === "object" ? pagination : {};
  const pageSize =
    typeof config.pageSize === "number" && config.pageSize > 0
      ? config.pageSize
      : DEFAULT_MOBILE_PAGE_SIZE;

  return {
    pageSize,
    total,
    showSizeChanger: false,
  };
};

/**
 * Responsive table: desktop table with horizontal scroll; mobile card list.
 */
export const CustomTable = ({
  tableHeaders,
  tableProps,
  footer,
  style,
  mobileCardConfig,
  forceMobileView = false,
  mobileDefaultSort,
}: CustomTableProps) => {
  const { t } = useAppTranslation();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [mobilePage, setMobilePage] = useState(1);

  const { dataSource, columns, loading, pagination, rowKey } = tableProps;

  const showMobileCards =
    (forceMobileView || isMobile) &&
    mobileCardConfig !== false &&
    columns &&
    columns.length > 0;

  const config: MobileCardConfig = {
    actionsKey: "actions",
    ...mobileCardConfig,
  };

  const getRowKey = (record: any, index: number): string | number => {
    if (typeof rowKey === "function") {
      const key = rowKey(record);
      return typeof key === "bigint" ? String(key) : key ?? index;
    }
    if (typeof rowKey === "string") {
      return record[rowKey] ?? index;
    }
    return record.key ?? record.id ?? index;
  };

  const mobileDataSource = useMemo(() => {
    if (!dataSource || !mobileDefaultSort) {
      return dataSource;
    }
    const sorted = [...dataSource];
    const factor = mobileDefaultSort.order === "ascend" ? 1 : -1;
    sorted.sort((a: any, b: any) => {
      const valueA = getNestedValue(a, mobileDefaultSort.dataIndex);
      const valueB = getNestedValue(b, mobileDefaultSort.dataIndex);
      if (typeof valueA === "number" && typeof valueB === "number") {
        return (valueA - valueB) * factor;
      }
      return (
        String(valueA ?? "").localeCompare(String(valueB ?? ""), undefined, {
          sensitivity: "base",
          numeric: true,
        }) * factor
      );
    });
    return sorted;
  }, [dataSource, mobileDefaultSort]);

  const mobilePagination = resolveMobilePagination(
    pagination,
    mobileDataSource?.length ?? 0,
  );

  const mobileTotalPages = mobilePagination
    ? Math.max(
        1,
        Math.ceil((mobileDataSource?.length ?? 0) / mobilePagination.pageSize),
      )
    : 1;
  const clampedMobilePage = Math.min(Math.max(mobilePage, 1), mobileTotalPages);

  const pagedMobileDataSource = useMemo(() => {
    if (!mobileDataSource) {
      return mobileDataSource;
    }
    if (!mobilePagination) {
      return mobileDataSource;
    }

    const pageSize = mobilePagination.pageSize;
    const start = (clampedMobilePage - 1) * pageSize;
    return mobileDataSource.slice(start, start + pageSize);
  }, [mobileDataSource, mobilePagination, clampedMobilePage]);

  return (
    <Card
      styles={{
        body: {
          display: "block",
          overflow: showMobileCards ? "visible" : "auto",
          padding: showMobileCards ? spacing.component : undefined,
        },
      }}
      style={style}
    >
      <div
        style={{
          marginBottom: showMobileCards ? spacing.component : "2rem",
          minWidth: 0,
          maxWidth: "100%",
        }}
      >
        {tableHeaders}
      </div>

      {showMobileCards ? (
        <div>
          {loading ? (
            <div style={{ textAlign: "center", padding: spacing.page }}>
              <Spin />
            </div>
          ) : !mobileDataSource || mobileDataSource.length === 0 ? (
            <div style={{ textAlign: "center", padding: spacing.page }}>
              <Text type="secondary">{t("No_data")}</Text>
            </div>
          ) : (
            <>
              {(pagedMobileDataSource ?? []).map((record, index) => {
                const pageOffset = mobilePagination
                  ? (clampedMobilePage - 1) * mobilePagination.pageSize
                  : 0;
                const key = getRowKey(record, pageOffset + index);
                return (
                  <MobileCardErrorBoundary key={key}>
                    <MobileCardItem
                      record={record}
                      columns={columns as ColumnsType<any>}
                      config={config}
                    />
                  </MobileCardErrorBoundary>
                );
              })}
              {mobilePagination &&
              mobileDataSource.length > mobilePagination.pageSize ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: spacing.component,
                  }}
                >
                  <Pagination
                    size="small"
                    current={clampedMobilePage}
                    pageSize={mobilePagination.pageSize}
                    total={mobilePagination.total}
                    showSizeChanger={false}
                    onChange={(page) => setMobilePage(page)}
                  />
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : (
        <Table bordered scroll={{ x: "max-content" }} {...tableProps} />
      )}

      {footer}
    </Card>
  );
};
